import type { RefObject } from "react";
import { useEffect, useRef } from "react";

/** Gap between the thumb and each end of the track, and its shortest length. */
const PAD = 8;
const MIN_HEIGHT = 28;

type ScrollThumb<T extends HTMLElement> = {
  /** The scroller. Its native bar has to be hidden in CSS. */
  listRef: RefObject<T>;
  /** An absolutely positioned sibling inside a `position: relative` wrapper. */
  thumbRef: RefObject<HTMLDivElement>;
};

/**
 * The design's custom scrollbar (design/CLAUDE.md, reference `.shdoc__list` +
 * `#shdoc-thumb`): the native bar is hidden and a thumb is drawn from
 * `scrollTop / scrollHeight / clientHeight` instead, and can be dragged. It
 * shows only while there is something to scroll (`is-vis`).
 *
 * The scroller's size changes with the window and its content with the data,
 * so both are observed rather than synced by hand.
 *
 * `mountKey` is for a scroller that is not in the DOM on the first render —
 * a portal waiting for its host: change it once the elements exist.
 */
export const useScrollThumb = <T extends HTMLElement = HTMLDivElement>(mountKey?: unknown): ScrollThumb<T> => {
  const listRef = useRef<T>(null);
  const thumbRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const list = listRef.current;
    const thumb = thumbRef.current;

    if (list === null || thumb === null) return;

    const sync = () => {
      const { clientHeight, scrollHeight, scrollTop } = list;

      if (scrollHeight <= clientHeight + 1) {
        thumb.classList.remove("is-vis");
        return;
      }

      const track = clientHeight - PAD * 2;
      const height = Math.max(MIN_HEIGHT, (track * clientHeight) / scrollHeight);
      const top = PAD + (track - height) * (scrollTop / (scrollHeight - clientHeight));

      thumb.style.height = `${height}px`;
      thumb.style.top = `${top}px`;
      thumb.classList.add("is-vis");
    };

    let drag: { y: number; top: number } | null = null;

    const onPointerDown = (event: PointerEvent) => {
      drag = { y: event.clientY, top: list.scrollTop };
      thumb.classList.add("is-drag");
      thumb.setPointerCapture(event.pointerId);
      event.preventDefault();
    };

    const onPointerMove = (event: PointerEvent) => {
      if (drag === null) return;

      const track = list.clientHeight - PAD * 2 - thumb.offsetHeight;

      if (track <= 0) return;

      list.scrollTop = drag.top + ((event.clientY - drag.y) * (list.scrollHeight - list.clientHeight)) / track;
    };

    const onPointerUp = () => {
      drag = null;
      thumb.classList.remove("is-drag");
    };

    const resized = new ResizeObserver(sync);
    const mutated = new MutationObserver(sync);

    list.addEventListener("scroll", sync);
    window.addEventListener("resize", sync);
    thumb.addEventListener("pointerdown", onPointerDown);
    thumb.addEventListener("pointermove", onPointerMove);
    thumb.addEventListener("pointerup", onPointerUp);
    thumb.addEventListener("pointercancel", onPointerUp);
    resized.observe(list);
    mutated.observe(list, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "hidden"] });
    sync();

    return () => {
      list.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
      thumb.removeEventListener("pointerdown", onPointerDown);
      thumb.removeEventListener("pointermove", onPointerMove);
      thumb.removeEventListener("pointerup", onPointerUp);
      thumb.removeEventListener("pointercancel", onPointerUp);
      resized.disconnect();
      mutated.disconnect();
    };
  }, [mountKey]);

  return { listRef, thumbRef };
};
